import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useArtworks } from '../../contexts/ArtworkContext';
import { uploadImage, deleteImage, extractImagePath } from '../../utils/imageUpload';
import AdminPageHeader from './AdminPageHeader';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  overflow-y: auto;
  height: 100vh;
`;

const Content = styled.div`
  padding: 2rem;
  max-width: 800px;
  margin: 0 auto;
`;

const Form = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2d2d2d;
    box-shadow: 0 0 0 2px rgba(45, 45, 45, 0.1);
  }
  
  &.error {
    border-color: #2d2d2d;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #2d2d2d;
    box-shadow: 0 0 0 2px rgba(45, 45, 45, 0.1);
  }
  
  &.error {
    border-color: #2d2d2d;
  }
`;

const FileInputWrapper = styled.div`
  position: relative;
`;

const FileInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2d2d2d;
  }
  
  &.error {
    border-color: #2d2d2d;
  }
`;

const ImagePreview = styled.div`
  margin-top: 1rem;
  
  img {
    max-width: 300px;
    max-height: 200px;
    object-fit: contain;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
`;

const ErrorMessage = styled.div`
  color: #2d2d2d;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  
  &.primary {
    background: #2d2d2d;
    color: white;
    
    &:hover:not(:disabled) {
      background: #1a1a1a;
    }
    
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #5a6268;
    }
  }
`;

const ArtworkForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { artworks, addArtwork, updateArtwork } = useArtworks();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    size: '',
    material: '',
    year: '',
    description: '',
    description_en: '',
    image: '',
    imagePath: ''
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { confirmNavigation, markSaved } = useUnsavedChanges(hasUnsavedChanges);

  useEffect(() => {
    if (isEdit && id) {
      const artwork = artworks.find(item => String(item.id) === String(id));
      if (artwork) {
        setFormData({
          title: artwork.title || '',
          size: artwork.size || '',
          material: artwork.material || '',
          year: artwork.year || '',
          description: artwork.description || '',
          description_en: artwork.description_en || '',
          image: artwork.image || '',
          imagePath: artwork.imagePath || ''
        });
        setImagePreview(artwork.image || '');
      }
    }
  }, [isEdit, id, artworks]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasUnsavedChanges(true);
    
    // 에러 메시지 제거
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // 파일 크기 확인 (5MB = 5 * 1024 * 1024 bytes)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          image: '이미지 파일 크기는 5MB 이하여야 합니다.'
        }));
        return;
      }

      // 파일 형식 확인
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          image: '이미지 파일만 업로드 가능합니다.'
        }));
        return;
      }

      setImageFile(file);
      setHasUnsavedChanges(true);
      setIsImageUploading(true);
      
      // 이미지 미리보기
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // 에러 메시지 제거
      if (errors.image) {
        setErrors(prev => ({
          ...prev,
          image: ''
        }));
      }

      // Firebase Storage에 이미지 업로드
      try {
        const uploadResult = await uploadImage(file, 'artworks');
        if (uploadResult.success) {
          setFormData(prev => ({
            ...prev,
            image: uploadResult.url,
            imagePath: uploadResult.path
          }));
          console.log('이미지 업로드 성공:', uploadResult.url);
        } else {
          setErrors(prev => ({
            ...prev,
            image: '이미지 업로드에 실패했습니다: ' + uploadResult.error
          }));
        }
      } catch (error) {
        console.error('이미지 업로드 오류:', error);
        setErrors(prev => ({
          ...prev,
          image: '이미지 업로드 중 오류가 발생했습니다.'
        }));
      } finally {
        setIsImageUploading(false);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = '작품 이름을 입력해주세요.';
    }

    if (!formData.size.trim()) {
      newErrors.size = '작품 크기를 입력해주세요.';
    }

    if (!formData.material.trim()) {
      newErrors.material = '제작 도구/재료를 입력해주세요.';
    }

    if (!formData.year.trim()) {
      newErrors.year = '제작 연도를 입력해주세요.';
    }

    if (!formData.image && !isImageUploading) {
      newErrors.image = '작품 이미지를 선택해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || isImageUploading) {
      return;
    }

    setIsSubmitting(true);

    try {
      const artworkData = {
        title: formData.title.trim(),
        size: formData.size.trim(),
        material: formData.material.trim(),
        year: formData.year.trim(),
        description: formData.description.trim(),
        description_en: formData.description_en.trim(),
        image: formData.image,
        imagePath: formData.imagePath
      };

      if (isEdit) {
        // 수정 시 기존 이미지가 변경되었다면 기존 이미지 삭제
        const existingArtwork = artworks.find(artwork => String(artwork.id) === String(id));
        if (existingArtwork && existingArtwork.imagePath && 
            existingArtwork.imagePath !== formData.imagePath && 
            formData.imagePath) {
          await deleteImage(existingArtwork.imagePath);
        }
        
        const result = await updateArtwork(id, artworkData);
        if (result.success) {
          alert('작품이 수정되었습니다.');
          markSaved();
          navigate('/admin/artwork');
        } else {
          alert('작품 수정에 실패했습니다: ' + result.error);
        }
      } else {
        const result = await addArtwork(artworkData);
        if (result.success) {
          alert('작품이 추가되었습니다.');
          markSaved();
          navigate('/admin/artwork');
        } else {
          alert('작품 추가에 실패했습니다: ' + result.error);
        }
      }
    } catch (error) {
      console.error('작품 저장 중 오류:', error);
      alert('작품 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (confirmNavigation()) navigate('/admin/artwork');
  };

  return (
    <Container>
      <AdminPageHeader
        title={isEdit ? '작품 수정' : '작품 추가'}
        backTo="/admin/artwork"
        onBack={handleCancel}
      />
      
      <Content>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="image">
              작품 이미지 (5MB 이하)
              {isImageUploading && ' - 업로드 중...'}
            </Label>
            <FileInput
              id="image"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className={errors.image ? 'error' : ''}
              disabled={isImageUploading}
            />
            {errors.image && <ErrorMessage>{errors.image}</ErrorMessage>}
            {imagePreview && (
              <ImagePreview>
                <img src={imagePreview} alt="미리보기" />
                {isImageUploading && <div>업로드 중...</div>}
              </ImagePreview>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="title">작품 이름</Label>
            <Input
              id="title"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className={errors.title ? 'error' : ''}
              placeholder="작품 이름을 입력하세요"
            />
            {errors.title && <ErrorMessage>{errors.title}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="size">작품 크기</Label>
            <Input
              id="size"
              type="text"
              name="size"
              value={formData.size}
              onChange={handleInputChange}
              className={errors.size ? 'error' : ''}
              placeholder="예: 100 x 80 cm"
            />
            {errors.size && <ErrorMessage>{errors.size}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="material">제작 도구/재료</Label>
            <Input
              id="material"
              type="text"
              name="material"
              value={formData.material}
              onChange={handleInputChange}
              className={errors.material ? 'error' : ''}
              placeholder="예: Oil on canvas"
            />
            {errors.material && <ErrorMessage>{errors.material}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="year">제작 연도</Label>
            <Input
              id="year"
              type="text"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              className={errors.year ? 'error' : ''}
              placeholder="예: 2024"
            />
            {errors.year && <ErrorMessage>{errors.year}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="description">
              작품 설명 (한국어, 선택사항) - {formData.description.length}/300자
            </Label>
            <TextArea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="작품에 대한 설명을 입력하세요 (최대 300자)"
              maxLength={300}
              className={errors.description ? 'error' : ''}
            />
            {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="description_en">
              작품 설명 (English, 선택사항) - {formData.description_en.length}/300자
            </Label>
            <TextArea
              id="description_en"
              name="description_en"
              value={formData.description_en}
              onChange={handleInputChange}
              placeholder="Enter artwork description in English (max 300 characters)"
              maxLength={300}
              className={errors.description_en ? 'error' : ''}
            />
            {errors.description_en && <ErrorMessage>{errors.description_en}</ErrorMessage>}
          </FormGroup>

          <FormActions>
            <Button 
              type="submit" 
              className="primary"
              disabled={isSubmitting || isImageUploading}
            >
              {isSubmitting ? '저장 중...' : isImageUploading ? '이미지 업로드 중...' : (isEdit ? '수정하기' : '추가하기')}
            </Button>
            <Button 
              type="button" 
              className="secondary" 
              onClick={handleCancel}
            >
              취소
            </Button>
          </FormActions>
        </Form>
      </Content>
    </Container>
  );
};

export default ArtworkForm;